import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type MouseEventHandler } from 'react'
import { useNavigate } from 'react-router-dom'
import { paymentsApi } from '../../../api/payments'
import { useAccountSegment } from '../../product-segment'
import { checkoutCoordinator, getPaymentChannel, paymentResultMessage } from '../../../payments/channel'
import type { PlanId, StoreProduct } from '../../../payments/types'
import * as metrica from '../../../lib/metrica'
import { useMessagingStore } from '../../../store/messagingStore'
import { toast } from '../../../store/toastStore'

export function usePricing() {
  const navigate = useNavigate()
  const channel = getPaymentChannel()
  const { profile, error: profileError, retry: retryProfile } = useAccountSegment()
  const [payError, setPayError] = useState('')
  const [payNotice, setPayNotice] = useState('')
  const [payingId, setPayingId] = useState<string | null>(null)
  const payingRef = useRef(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pricing'],
    queryFn: async () => {
      const plansData = await paymentsApi.plans()
      try {
        return { plans: plansData.plans || [], products: await channel.getProducts(), catalogError: '' }
      } catch (error) {
        if (channel.provider !== 'rustore') throw error
        return { plans: plansData.plans || [], products: [] as StoreProduct[], catalogError: paymentResultMessage(error) }
      }
    },
  })

  useEffect(() => {
    if (data) metrica.reachGoal('pricing_page_view')
  }, [data])

  const pay = async (planId: string) => {
    if (payingRef.current) return
    payingRef.current = true
    setPayError('')
    setPayNotice('')
    const claim = checkoutCoordinator.claim(channel, planId as PlanId)
    if (claim.kind === 'busy') {
      setPayError(paymentResultMessage({ code: 'purchase_in_progress' }))
      payingRef.current = false
      return
    }
    setPayingId(planId)
    if (claim.kind === 'acceptedNew') {
      metrica.reachGoal('tariff_click', { plan: planId })
      metrica.reachGoal(`tariff_click_${planId}`)
    }
    try {
      const outcome = await claim.promise
      if (channel.provider === 'rustore' && outcome.status === 'completed') {
        useMessagingStore.getState().clearPaywall()
        toast('Покупка подтверждена', 'success')
        navigate('/')
        return
      }
      setPayNotice(paymentResultMessage(outcome))
    } catch (error) {
      setPayError(paymentResultMessage(error))
    } finally {
      setPayingId(null)
      payingRef.current = false
    }
  }

  const handlePay: MouseEventHandler<HTMLButtonElement> = (event) => {
    void pay(event.currentTarget.value)
  }
  return { channel, profile, profileError, retryProfile, data, isLoading, isError, payError, payNotice, payingId, handlePay }
}

export type PricingState = ReturnType<typeof usePricing>
